import React from 'react';
import { useTranslation } from 'react-i18next';

import { asset } from '../../lib/asset';
import { PreviewPlaceholder } from '../wizard/PreviewPlaceholder';
import { PHONE_FRAME_DEFAULT, PHONE_RADIUS_RATIO } from '../../steps/finish/components/DevicePreview/frame-art';
import {
  PhoneRoot,
  PhoneScreen,
  PhoneTopBar,
  PhoneBottomBar,
  ZoomLayer,
  FrameContent,
  FillIframe,
  FrameOverlayImg,
} from './PreviewStage.styles';

export type PhoneDeviceProps = {
  /** Live site URL (the stable iframe's only `src`; reloads ONLY when this changes). */
  src: string;
  /** Status-bar strip + screen base tint (selected colour). */
  topColor?: string;
  /** Home-indicator strip tint (selected colour); the host hands the loading tint. */
  bottomColor?: string;
  /** Brand body background (the flat colour the loading placeholder fills with). */
  bgColor?: string;
  /** Render the page at this virtual width then scale to fit (zoom out). Omit = 1:1. */
  zoomWidth?: number;
  /** Bare = no bezel: the screen fills the wrapper (the full-screen mobile preview). */
  bare?: boolean;
  /** Host-owned reveal gate (this iframe's `page-rendered`, else load + settle). */
  shown: boolean;
  onLoad: () => void;
  /** Same gate's ref: it identifies THIS iframe's window, so the gate can tell our
   *  `page-rendered` message apart from the mac iframe's (same origin, same URL). */
  iframeRef?: React.Ref<HTMLIFrameElement>;
};

/**
 * The persistent mobile preview device: an iPhone bezel wrapping the wizard's one
 * warm "phone-role" iframe. The iframe ALWAYS lives inside the Zoom layer (so a 1:1
 * ↔ zoomed-out swap never re-parents it) and is authored once — switching surfaces,
 * zoom, or going bare for full-screen only rewrites CSS, never reloads. Ported from
 * the finish DevicePreview PhoneFrame.
 */
export const PhoneDevice = ({
  src,
  topColor,
  bottomColor,
  bgColor,
  zoomWidth,
  bare = false,
  shown,
  onLoad,
  iframeRef,
}: PhoneDeviceProps): React.ReactElement => {
  const { t } = useTranslation();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [radius, setRadius] = React.useState(42);
  const [content, setContent] = React.useState<{ w: number; h: number }>({ w: 0, h: 0 });

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const measure = (): void => {
      setRadius(root.getBoundingClientRect().width * PHONE_RADIUS_RATIO);
      const c = contentRef.current;
      if (c) {
        const r = c.getBoundingClientRect();
        setContent({ w: r.width, h: r.height });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, []);
  // `content` drives the zoom scale (the flex screen region between the status / home
  // strips). Measured with getBoundingClientRect, not offsetWidth: the latter rounds to
  // an integer, and `scale = content.w / virtualWidth` then sizes the framed document a
  // fraction of a pixel wide of the cut-out, pushing its right edge under the clip.

  // Bare full-screen + the 1:1 inline phone never zoom; only the finish group does.
  const virtualWidth = !bare && zoomWidth ? zoomWidth : content.w || 1;
  const scale = content.w > 0 ? content.w / virtualWidth : 0;
  const zoomHeight = scale > 0 ? `${content.h / scale}px` : '100%';

  return (
    <PhoneRoot ref={rootRef}>
      <PhoneScreen
        $radius={radius}
        $bg={topColor}
        $bare={bare}
        $left={PHONE_FRAME_DEFAULT.screen.left}
        $top={PHONE_FRAME_DEFAULT.screen.top}
        $width={PHONE_FRAME_DEFAULT.screen.width}
        $height={PHONE_FRAME_DEFAULT.screen.height}
      >
        <PhoneTopBar $color={topColor} $bare={bare} />
        <FrameContent ref={contentRef}>
          <ZoomLayer $width={virtualWidth} $height={zoomHeight} $scale={scale || 0.0001}>
            <FillIframe
              ref={iframeRef}
              title={t('Mobile preview')}
              src={src || undefined}
              onLoad={onLoad}
              data-cfg-preview="phone"
              $shown={shown && scale > 0}
            />
          </ZoomLayer>
        </FrameContent>
        <PhoneBottomBar $color={shown ? bottomColor : topColor} $bare={bare} />
        <PreviewPlaceholder shown={!shown} bgColor={bgColor} />
      </PhoneScreen>
      <FrameOverlayImg src={asset(PHONE_FRAME_DEFAULT.src)} alt="" $hidden={bare} />
    </PhoneRoot>
  );
};

export default PhoneDevice;
